import {
  createContext, PropsWithChildren, useContext, useEffect,
  useState,
} from 'react';
import { useMatch } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { useWebSocketContext } from '../WebSocketProvider';
import { TChestHistoryResponse } from '../../types';
import { TPaymentWsMessage } from '../WebSocketProvider/types';
import { PaymentEvents } from '../../constants/events';
import { useAppDispatch } from '../../store';
import { paymentSucceeded } from '../../store/reducers/payments';
import Pages from '../../Router/pages';
import SpinAndClaimRewards from '../../templates/SpinAndClaimRewards';
import { updateProfileCoins, updateProfileEnergy } from '../../store/reducers/profile/asyncActions';

type TPaymentProviderProps = PropsWithChildren

enum EModalType {
    CHEST = 'chest'
}

type TChestSpinnerModal = {
    type: EModalType.CHEST;
    data: {
        chestHistory: TChestHistoryResponse
    }
}

type TModal = TChestSpinnerModal

const PaymentContext = createContext({});

export const usePaymentContext = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error(
      'useRocketCoinsContext must be used within an CoinsProvider',
    );
  }

  return context;
};

function PaymentProvider({ children }: TPaymentProviderProps) {
  const dispatch = useAppDispatch();

  const { webSocket } = useWebSocketContext();

  const match = useMatch(Pages.chest());

  const [modal, setModal] = useState<TModal>();

  useEffect(() => {
    const onMessage = async (evt: MessageEvent<string>) => {
      const msg: TPaymentWsMessage = JSON.parse(evt.data);

      switch (msg.type) {
        case 'chest-owned':
          if (!match) {
            setModal({
              type: EModalType.CHEST,
              data: {
                chestHistory: msg.data.chest_history,
              },
            });
          } else {
            dispatchEvent(new CustomEvent(PaymentEvents.CHEST_OWNED, {
              detail: {
                chestHistory: msg.data.chest_history,
              },
            }));
          }

          dispatch(paymentSucceeded({ id: msg.data.payment_id }));
          break;
        case 'store-item-owned':
          dispatch(updateProfileCoins({ coins: msg.data.owned.coins, replace: false }));
          dispatch(updateProfileEnergy({ energy: msg.data.owned.energy, replace: false }));

          dispatch(paymentSucceeded({ id: msg.data.payment_id }));
          break;
        case 'purchase-failed':
          enqueueSnackbar('Purchase failed', { variant: 'error' });
          break;
        default:
          break;
      }
    };

    webSocket.server?.addEventListener('message', onMessage);

    return () => {
      webSocket.server?.removeEventListener('message', onMessage);
    };
  }, [!!match]);

  const onClose = () => {
    setModal(undefined);
  };

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <PaymentContext.Provider value={{}}>
      {modal?.type === EModalType.CHEST && <SpinAndClaimRewards chestHistory={modal.data.chestHistory} onCollectClose={onClose} />}
      {children}
    </PaymentContext.Provider>
  );
}

export default PaymentProvider;
